import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Search, Trash2, Edit, MapPin, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import PageLayout from "@/components/PageLayout";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VisitsPage() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    date: new Date().toISOString().split('T')[0],
    notes: "",
    follow_up_date: ""
  });

  useEffect(() => {
    fetchVisits();
  }, []);

  const fetchVisits = async (search = "") => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/visits`, { 
        params,
        withCredentials: true 
      });
      setVisits(response.data);
    } catch (error) {
      console.error("Failed to fetch visits", error);
      toast.error("Failed to load visits");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    fetchVisits(query);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/visits`, formData, { withCredentials: true });
      toast.success("Visit recorded successfully");
      setDialogOpen(false);
      setFormData({
        name: "",
        address: "",
        date: new Date().toISOString().split('T')[0],
        notes: "",
        follow_up_date: ""
      });
      fetchVisits();
    } catch (error) {
      console.error("Failed to create visit", error);
      toast.error("Failed to record visit");
    }
  };

  const deleteVisit = async (visitId) => {
    if (!window.confirm("Are you sure you want to delete this visit?")) return;
    
    try {
      await axios.delete(`${API}/visits/${visitId}`, { withCredentials: true });
      toast.success("Visit deleted");
      fetchVisits();
    } catch (error) {
      console.error("Failed to delete visit", error);
      toast.error("Failed to delete visit");
    }
  };

  return (
    <PageLayout title="Personal Visits">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="search-visits-input"
            type="text"
            placeholder="Search visits..."
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-visit-button" className="bg-emerald-700 hover:bg-emerald-800">
              <Plus className="w-4 h-4 mr-2" />
              Record Visit
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record Personal Visit</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <Input
                  data-testid="visit-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Person or family name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <Input
                  data-testid="visit-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Visit location"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Visit Date</label>
                <Input
                  data-testid="visit-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <Textarea
                  data-testid="visit-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Discussion points, prayer requests, observations..."
                  rows={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Follow-up Date (Optional)</label>
                <Input
                  data-testid="visit-followup-input"
                  type="date"
                  value={formData.follow_up_date}
                  onChange={(e) => setFormData({...formData, follow_up_date: e.target.value})}
                />
              </div>
              <Button data-testid="submit-visit-button" type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                Record Visit
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
        </div>
      ) : visits.length === 0 ? (
        <div className="text-center py-12">
          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No visits recorded yet</p>
          <p className="text-sm text-gray-400 mt-1">Start tracking your personal visits</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {visits.map(visit => (
            <VisitCard 
              key={visit.id}
              visit={visit}
              onDelete={() => deleteVisit(visit.id)}
            />
          ))}
        </div>
      )}
    </PageLayout>
  );
}

function VisitCard({ visit, onDelete }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{visit.name}</h3>
          {visit.address && (
            <div className="flex items-center text-sm text-gray-600 mb-2">
              <MapPin className="w-4 h-4 mr-1" />
              {visit.address}
            </div>
          )}
          <div className="flex items-center text-sm text-gray-500 mb-3">
            <Calendar className="w-4 h-4 mr-1" />
            {format(new Date(visit.date), 'MMMM d, yyyy')}
          </div>
          {visit.notes && (
            <p className="text-sm text-gray-700 mb-3 bg-gray-50 p-3 rounded-lg">{visit.notes}</p>
          )}
          {visit.follow_up_date && (
            <div className="inline-flex items-center text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-800">
              Follow-up: {format(new Date(visit.follow_up_date), 'MMM d, yyyy')}
            </div>
          )}
        </div>
        <Button
          data-testid={`visit-delete-${visit.id}`}
          onClick={onDelete}
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
